/**
 * The drawer opens a failed session on its cause (ADR 0059 decision 1): Failed, why in one
 * sentence, and the one fix the cause offers, then the transcript. A model that does not exist
 * retries with one that does, and names where it was set (the mission). Blocked sessions have
 * their own card (ApprovalBlocker, Approve and resume).
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import type { Session, SessionFailure } from '@orgus/protocol';
import { FAILURE_CAUSE_LABEL, agentKindOf } from '@orgus/protocol';
import { Button, StatusPill, Tooltip, formatClock, toUiError, toast } from '@orgus/ui';
import type { UiError } from '@orgus/ui';
import { RotateCcw, SquareTerminal } from '@orgus/ui/icons';
import { runAction } from '../../lib/api.ts';
import { useMachine, useOrgus } from '../../lib/store.ts';
import { ResumeError } from '../approvals/ApproveAndResumeButton.tsx';
import { startMission, useMissionsState } from '../missions/data.ts';
import { failureOf, fixFor } from './failures.ts';
import type { FailureFix } from './failures.ts';
import { provenModels } from './labels.ts';
import { missionIdOfTitle } from './shownTitle.ts';

/** The card body: pure enough to render in tests. */
export function FailureCardView({
  failure,
  fix,
  source,
  busy,
  error,
  onFix,
}: {
  failure: SessionFailure;
  fix: FailureFix;
  /** Where the cause was set (the mission), to fix it there. */
  source?: { label: string; open: () => void } | null;
  busy?: boolean;
  error?: UiError | null;
  onFix?: () => void;
}) {
  const [showDetail, setShowDetail] = useState(false);
  const detail = failure.detail && failure.detail !== failure.summary ? failure.detail : null;
  return (
    <div role="group" aria-label={`Failed: ${FAILURE_CAUSE_LABEL[failure.cause]}`} className="flex flex-col gap-2 rounded-card border border-border bg-surface p-3 shadow-elevation-flat">
      <div className="flex items-center gap-2">
        <StatusPill status="failed" label="Failed" variant="plain" />
        <span className="truncate text-caption text-fg-subtle">{FAILURE_CAUSE_LABEL[failure.cause]}</span>
      </div>
      <p className="text-body text-fg">{failure.summary}</p>
      {fix.note && <p className="text-ui text-fg-muted">{fix.note}</p>}
      {(fix.action !== 'none' || source || detail) && (
        <div className="flex flex-wrap items-center gap-2 pt-1">
          {fix.action !== 'none' && (
            <Tooltip content={fix.disabled}>
              <span tabIndex={fix.disabled ? 0 : -1}>
                <Button size="sm" variant="primary" icon={fix.action === 'terminal' ? <SquareTerminal /> : <RotateCcw />} loading={busy} disabled={!!fix.disabled || busy} onClick={onFix}>
                  {fix.label}
                </Button>
              </span>
            </Tooltip>
          )}
          {source && (
            <Button size="sm" variant="ghost" onClick={source.open}>
              {source.label}
            </Button>
          )}
          {detail && (
            <Button size="sm" variant="ghost" aria-expanded={showDetail} onClick={() => setShowDetail((v) => !v)}>
              {failure.cause === 'task' ? (showDetail ? 'Hide its last message' : 'Show its last message') : showDetail ? 'Hide the error' : 'Show the error'}
            </Button>
          )}
        </div>
      )}
      {detail && showDetail && <pre className="max-h-40 overflow-auto rounded-control bg-sunken p-2 font-mono text-caption whitespace-pre-wrap break-all text-fg-muted">{detail}</pre>}
      {error && <ResumeError error={error} onRetry={onFix} />}
    </div>
  );
}

/** Mount: SessionDrawer's Activity tab, first. Renders nothing for a session that did not fail. */
export function FailureCard({ session: s, onOpenTerminal }: { session: Session; onOpenTerminal: () => Promise<void> | void }) {
  const failure = failureOf(s);
  const machine = useMachine(s.machineId);
  const sessions = useOrgus((st) => st.sessions);
  const missions = useMissionsState().state?.missions;
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<UiError | null>(null);
  const missionId = missionIdOfTitle(s.title);
  const mission = missionId ? missions?.find((m) => m.id === missionId) : undefined;
  const launch = mission?.launches.find((l) => l.sessionId === s.id) ?? null;
  const proven = useMemo(() => provenModels(Object.values(sessions).filter((x) => x.machineId === s.machineId)), [sessions, s.machineId]);
  if (!failure || failure.cause === 'blocked') return null;
  const machineName = machine?.name ?? 'its machine';
  const fix = fixFor(failure, s, { machineName, proven, now: Date.now(), clock: formatClock });
  const modelSetOnMission = failure.cause === 'model-unavailable' && !!mission && (launch?.model ?? mission.model) != null;
  const source = mission ? { label: modelSetOnMission ? 'Change the model on the mission' : 'Open the mission', open: () => navigate(`/missions/${encodeURIComponent(mission.id)}`) } : null;

  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      if (fix.action === 'terminal') {
        await onOpenTerminal();
      } else if (mission && launch && fix.action === 'retry') {
        // A mission's run retries as a new run of the mission, where it was launched.
        await startMission(mission.id, {
          machineId: launch.machineId,
          ...(launch.checkoutId ? { checkoutId: launch.checkoutId } : launch.cwd ? { cwd: launch.cwd } : { cwd: s.cwd }),
          mode: launch.mode,
          model: fix.model ?? launch.model ?? mission.model ?? null,
        });
        toast.success('Mission retried', `A new run started on ${machineName}.`);
      } else {
        // Everything else continues the same conversation, in its own folder (headless resume).
        const r = await runAction(s.machineId, 'session.start', {
          cwd: s.cwd,
          mode: 'headless',
          resumeSessionId: s.id,
          prompt: fix.prompt ?? 'Continue the task above.',
          ...(fix.model ? { model: fix.model } : s.model ? { model: s.model } : {}),
          agentKind: agentKindOf(s),
        });
        toast.success(fix.action === 'retry' ? 'Session retried' : 'Session resumed', r.message ?? `It continues on ${machineName}.`);
      }
    } catch (e) {
      setError(toUiError(e, { what: `Could not ${fix.label.toLowerCase()}.`, next: 'Check that the machine is online, then try again.' }));
    } finally {
      setBusy(false);
    }
  };

  return <FailureCardView failure={failure} fix={fix} source={source} busy={busy} error={error} onFix={() => void run()} />;
}
